import { useContext, useState, KeyboardEvent, ChangeEvent } from "react";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { BulkGenerateSerialsSidebar } from "@components/shared/BulkGenerateSerialsSidebar/BulkGenerateSerialsSidebar";
import UiContext from "@contexts/UiContext";
import IPO_Shipment_Catalog_Item_MapUI from "@models/IPO_Shipment_Catalog_Item_MapUI";
import { ISerialEntryItemState } from "@hooks/purchase_orders/usePurchaseOrderShipmentSerials";
import "./POShipmentSerialEntry.scss";

interface POShipmentSerialEntryProps {
    row: IPO_Shipment_Catalog_Item_MapUI;
    serialState: ISerialEntryItemState;
    isProcessed: boolean;
    maxSerials: number;
    onAddSerial: (catalogItemId: number, value: string) => void;
    onBulkAddSerials: (catalogItemId: number, values: string[]) => void;
    onRemoveSerial: (catalogItemId: number, index: number) => void;
    onDeleteSerial: (catalogItemId: number, serialId: number) => void;
    onSubmitSerials: (catalogItemId: number, catalogItemMapId: number) => void;
}

const POShipmentSerialEntry = ({
    row,
    serialState,
    isProcessed,
    maxSerials,
    onAddSerial,
    onBulkAddSerials,
    onRemoveSerial,
    onDeleteSerial,
    onSubmitSerials,
}: POShipmentSerialEntryProps) => {
    const uiContext = useContext(UiContext);
    const [inputValue, setInputValue] = useState("");

    const catalogItemId = row.catalog_item_id;
    const totalCount = serialState.serials.length + serialState.pendingSerials.length;
    const isAtMax = totalCount >= maxSerials;

    const hasValidSerials = serialState.pendingSerials.some((p) => p.isValid === true);
    const hasValidating = serialState.pendingSerials.some((p) => p.isValid === null);
    const hasInvalid = serialState.pendingSerials.some((p) => p.isValid === false);
    const canSubmit = hasValidSerials && !hasValidating && !serialState.isSubmitting;

    const handleAdd = () => {
        const trimmed = inputValue.trim();
        if (!trimmed) return;

        // Check for local duplicates
        const existingPending = serialState.pendingSerials.some((p) => p.value === trimmed);
        const existingCreated = serialState.serials.some((s) => s.value === trimmed);
        if (existingPending || existingCreated) return;

        onAddSerial(catalogItemId, trimmed);
        setInputValue("");
    };

    const handleInputChange = (e: ChangeEvent<any>) => {
        setInputValue(e.target.value);
    };

    const handleKeyDown = (e: KeyboardEvent<any>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAdd();
        }
    };

    const handleSubmit = () => {
        onSubmitSerials(catalogItemId, row.id);
    };

    const closeBulkSidebar = () => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);
    };

    const openBulkSidebar = () => {
        uiContext.setSidebarComponent(
            <BulkGenerateSerialsSidebar
                cancel={closeBulkSidebar}
                maxQty={maxSerials - totalCount}
                onAdd={(serials) => {
                    onBulkAddSerials(catalogItemId, serials);
                    closeBulkSidebar();
                }}
            />
        );
        uiContext.toggleSidebar(true);
    };

    const getBadgeClass = (isValid: boolean | null): string => {
        if (isValid === null) return "bg-secondary";
        if (isValid === true) return "bg-primary";
        return "bg-danger";
    };

    const getValidationIcon = (isValid: boolean | null): string | null => {
        if (isValid === null) return "fas fa-spinner fa-spin";
        if (isValid === true) return "fas fa-check";
        if (isValid === false) return "fas fa-exclamation-triangle";
        return null;
    };

    // Read-only view after finalization
    if (isProcessed) {
        return (
            <div className="POShipmentSerialEntry">
                <div className="mb-2">
                    <OwLabel title={"serials"} className="fw-bold" noBottomMargin />
                </div>
                {serialState.serials.length > 0 ? (
                    <div className="serial-badges">
                        {serialState.serials.map((serial) => (
                            <span key={serial.id} className="badge bg-success me-2 mb-1">
                                {serial.value}
                            </span>
                        ))}
                    </div>
                ) : (
                    <span className="text-muted">No serials recorded</span>
                )}
            </div>
        );
    }

    return (
        <div className="POShipmentSerialEntry">
            <div className="mb-2">
                <OwLabel title={"serials"} className="fw-bold" noBottomMargin />
                <span className="text-muted ms-2">
                    ({totalCount} / {maxSerials})
                </span>
            </div>

            {/* Input row */}
            <div className="d-flex align-items-start mb-2">
                <div className="me-2" style={{ minWidth: "200px" }}>
                    <OwInputWrapper
                        name="serial_input"
                        value={inputValue}
                        fn={handleInputChange}
                        onKeyDownFn={handleKeyDown}
                        disabled={isAtMax}
                        placeholder="serial"
                    />
                </div>
                <div className="me-2">
                    <OwButton title={"add"} fn={handleAdd} disabled={isAtMax || !inputValue.trim()} />
                </div>
                <div className="me-2">
                    <OwButton title={"bulk_add"} fn={openBulkSidebar} disabled={isAtMax} />
                </div>
                {serialState.pendingSerials.length > 0 && (
                    <div>
                        <OwButton title={"save_serials"} fn={handleSubmit} disabled={!canSubmit} />
                    </div>
                )}
            </div>

            {/* Server errors from processPOShipmentItemSerials */}
            {serialState.errors.length > 0 && (
                <div className="mb-2">
                    {serialState.errors.map((error, i) => (
                        <div key={i} className="text-danger text-xs">
                            {error}
                        </div>
                    ))}
                </div>
            )}

            {/* Pending serials with validation state */}
            {serialState.pendingSerials.length > 0 && (
                <div className="serial-badges mb-2">
                    {serialState.pendingSerials.map((pending, index) => {
                        const icon = getValidationIcon(pending.isValid);
                        return (
                            <span
                                key={`pending-${index}`}
                                className={`badge ${getBadgeClass(pending.isValid)} me-2 mb-1 serial-badge`}
                                title={pending.error || ""}>
                                {icon && <i className={`${icon} me-1`} />}
                                {pending.value}
                                <i
                                    className="fas fa-times-circle ms-1 serial-remove"
                                    onClick={() => onRemoveSerial(catalogItemId, index)}
                                />
                            </span>
                        );
                    })}
                </div>
            )}

            {/* Inline validation errors for invalid serials */}
            {hasInvalid && (
                <div className="mb-2">
                    {serialState.pendingSerials
                        .filter((p) => p.isValid === false && p.error)
                        .map((p, i) => (
                            <div key={i} className="text-danger text-xs">
                                {p.value}: {p.error}
                            </div>
                        ))}
                </div>
            )}

            {/* Created serials */}
            {serialState.serials.length > 0 && (
                <div className="serial-badges">
                    {serialState.serials.map((serial) => (
                        <span key={serial.id} className="badge bg-success me-2 mb-1 serial-badge">
                            {serial.value}
                            <i
                                className="fas fa-times-circle ms-1 serial-remove"
                                onClick={() => onDeleteSerial(catalogItemId, serial.id)}
                            />
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default POShipmentSerialEntry;
